import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Panel } from '@/ui';
import { useSession } from '@/hooks';

const Wrap = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 32px;
`;
// Solid Panel: this page renders full-canvas over the live WebGL backdrop.
const Card = styled(Panel).attrs({ $brackets: false })`
  max-width: 440px; width: 100%; display: flex; flex-direction: column; gap: 14px; text-align: center;
`;
const Title = styled.h1`
  margin: 0; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg}; color: ${({ theme }) => theme.color.textPrimary};
`;
const Body = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};
`;
const Cta = styled(Link)`
  display: inline-flex; align-items: center; justify-content: center; margin-top: 6px;
  font-weight: 600; font-size: ${({ theme }) => theme.fontSize.sm}; padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.md}; text-decoration: none; color: ${({ theme }) => theme.color.onAccent};
  background: ${({ theme }) => theme.color.accentCyan};
`;

type State = 'pending' | 'success' | 'invalid' | 'missing';

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { verifyEmail } = useSession();
  const [state, setState] = useState<State>(token ? 'pending' : 'missing');
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // guard React 18 StrictMode double-invoke
    verifyEmail(token).then(() => setState('success')).catch(() => setState('invalid'));
  }, [token, verifyEmail]);

  return (
    <Wrap>
      <Card>
        {state === 'pending' && (<><Title>Verifying…</Title><Body>Confirming your email address.</Body></>)}
        {state === 'success' && (
          <>
            <Title>Email verified</Title>
            <Body>Your email is confirmed. You can now post and interact on TruX.</Body>
            <Cta to="/">Go to TruX</Cta>
          </>
        )}
        {state === 'invalid' && (
          <>
            <Title>Couldn&apos;t verify</Title>
            <Body>This link is invalid or has expired. Sign in and resend a fresh link from the banner or Settings.</Body>
            <Cta to="/signin">Go to sign in</Cta>
          </>
        )}
        {state === 'missing' && (
          <>
            <Title>Couldn&apos;t verify</Title>
            <Body>No verification token was provided. Open the link from your email, or resend one after signing in.</Body>
            <Cta to="/signin">Go to sign in</Cta>
          </>
        )}
      </Card>
    </Wrap>
  );
}
